import { useEffect, useMemo, useState } from 'react';
import { MessageCircle } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { useClientWhatsApp } from '../../hooks/useWhatsApp';
import { WhatsAppTranscript } from '../whatsapp/WhatsAppTranscript';
import { SkeletonTable } from '../ui/Skeleton';

/**
 * Tab „WhatsApp” on a client (WhatsApp module): the client's WhatsApp groups,
 * copied automatically every few hours. Read-only; a client with several
 * groups gets a picker. Newest messages first load, „Mesaje mai vechi” pages
 * back.
 */
export function ClientWhatsAppTab({ client }: { client: { id: string; name: string } }) {
  const { t, locale } = useI18n();
  const [groupId, setGroupId] = useState<string | undefined>(undefined);
  const query = useClientWhatsApp(client.id, groupId);

  const first = query.data?.pages[0];
  const groups = first?.groups ?? [];
  const activeId = groupId ?? first?.groupId ?? undefined;
  const active = groups.find((g) => g.id === activeId);

  // Pages are newest-first; each page is oldest → newest inside.
  const messages = useMemo(
    () => [...(query.data?.pages ?? [])].reverse().flatMap((p) => p.messages),
    [query.data],
  );

  // A different client starts on its own first group.
  useEffect(() => setGroupId(undefined), [client.id]);

  const synced = active?.lastSyncedAt
    ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
        new Date(active.lastSyncedAt),
      )
    : null;

  if (query.isLoading) return <SkeletonTable />;
  if (query.isError) {
    return <p className="text-sm text-red-600">{t('whatsapp.client.loadError')}</p>;
  }

  if (groups.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
        {t('whatsapp.client.empty')}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <MessageCircle size={16} className="text-green-600" />
          {groups.length > 1 ? (
            <select
              value={activeId}
              onChange={(e) => setGroupId(e.target.value)}
              className="ui-input h-8 py-0 text-sm"
              aria-label={t('whatsapp.client.group')}
            >
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          ) : (
            <span>{groups[0].name}</span>
          )}
        </h3>
        <p className="text-xs text-gray-500">
          {synced ? t('whatsapp.client.synced', { when: synced }) : t('whatsapp.client.notSynced')}
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-card px-4 pb-4">
        {query.hasNextPage && (
          <div className="pt-3 text-center">
            <button
              type="button"
              onClick={() => void query.fetchNextPage()}
              disabled={query.isFetchingNextPage}
              className="text-sm text-brand-600 hover:underline disabled:opacity-50"
            >
              {query.isFetchingNextPage ? t('whatsapp.client.loading') : t('whatsapp.client.older')}
            </button>
          </div>
        )}
        {messages.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">
            {t('whatsapp.client.noMessages')}
          </p>
        ) : (
          <WhatsAppTranscript messages={messages} />
        )}
      </div>
    </div>
  );
}
